import { IListItem } from '@nkr/models/index';
import { AdditionalWeeksReason, WageAndTravelReimbursementRequestStatus } from '@nkr/enums/index';
import { getAdditionalWeekPolicyRule, getAnchorWeekForSharedComplicationsReport, getRequiredDocumentsForCenterApproval, isReasonAllowedForCenterApproval, requiresPhysicianInitialsForCenterApproval } from '@nkr/services/additional-week-policy';
import { IItem } from './types';

export const reasonIsValidForAllWeeks = (item: IItem): boolean => {
    if (item.status !== WageAndTravelReimbursementRequestStatus.CenterApproved) {
        return true;
    }

    for (const week of item.weeks) {
        const rule = getAdditionalWeekPolicyRule(item.organType, week);
        if (!rule.isSupported) {
            return false;
        }
        if (!isReasonAllowedForCenterApproval(item.organType, week, item.additionalWeeksReason)) {
            return false;
        }
        if (rule.requiresReasonForCenterApproval && !item.additionalWeeksReason) {
            return false;
        }
    }
    return true;
};

export const reasonTextValid = (item: IItem): boolean => {
    if (item.status !== WageAndTravelReimbursementRequestStatus.CenterApproved || !item.additionalWeeksReason) {
        return true;
    }
    if (item.additionalWeeksReason == AdditionalWeeksReason.Other) {
        return (item.additionalWeeksReasonOther?.trim().length || 0) >= 5;
    }
    if (item.additionalWeeksReason == AdditionalWeeksReason.ManualLabor) {
        return (item.additionalWeeksJobDescription?.trim().length || 0) >= 5;
    }
    return true;
};

export const requiresPhysicianInitialsForItem = (item: IItem): boolean => {
    if (item.status !== WageAndTravelReimbursementRequestStatus.CenterApproved) {
        return false;
    }
    for (const week of item.weeks) {
        if (requiresPhysicianInitialsForCenterApproval(item.organType, week, item.additionalWeeksReason)) {
            return true;
        }
    }
    return false;
};

export const getAllowedReasonsForItem = (item: IItem, additionalWeeksReasonList: IListItem[]): { requiresReason: boolean; reasons: IListItem[]; reasonMissing: boolean } => {
    const weeksRequested = item.weeks != null ? item.weeks : [];
    const reasonRules = weeksRequested.map((week) => getAdditionalWeekPolicyRule(item.organType, week));
    const requiresReason = reasonRules.some((r) => r.requiresReasonForCenterApproval);
    let reasons = additionalWeeksReasonList;
    if (requiresReason) {
        const allowedIds = reasonRules
            .filter((r) => r.requiresReasonForCenterApproval)
            .reduce<number[]>((acc, r) => {
                const ids = r.allowedReasons.map(x => x as number);
                return acc.length === 0 ? ids : acc.filter(x => ids.includes(x));
            }, []);
        reasons = reasons.filter(x => allowedIds.includes(x.id));
    }

    const isApproved = item.status === WageAndTravelReimbursementRequestStatus.CenterApproved;
    const reasonMissing = isApproved && requiresReason
        && (item.additionalWeeksReason === undefined || item.additionalWeeksReason === null);

    return { requiresReason, reasons, reasonMissing };
};

export const physicianLettersSatisfiedForItem = (item: IItem, sharedComplicationsReportByRequest: Record<string, File | null>): boolean => {
    if (item.status !== WageAndTravelReimbursementRequestStatus.CenterApproved) {
        return true;
    }
    const anchor = getAnchorWeekForSharedComplicationsReport(item.organType, item.weeks, item.additionalWeeksReason);
    if (anchor == null) {
        return true;
    }
    if (item.hasActiveComplicationsReport) {
        return true;
    }
    const file = sharedComplicationsReportByRequest[String(item.requestId)] ?? null;
    if (!file || file.size === 0) {
        return false;
    }
    const lower = file.name.toLowerCase();
    return file.type === 'application/pdf' || lower.endsWith('.pdf');
};

export const isReviewSubmittable = (
    item: IItem | null,
    physicianInitials: string,
    sharedComplicationsReportByRequest: Record<string, File | null>
): boolean => {
    if (!item) {
        return false;
    }
    if (!reasonIsValidForAllWeeks(item) || !reasonTextValid(item)) {
        return false;
    }
    if (item.status === WageAndTravelReimbursementRequestStatus.PendingApproval) {
        return false;
    }
    return (!requiresPhysicianInitialsForItem(item) || physicianInitials.trim().length > 1)
        && physicianLettersSatisfiedForItem(item, sharedComplicationsReportByRequest);
};
